import { useEffect, useState, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import VitalsForm, { bpFlag } from '../components/vitals/VitalsForm'
import NurseAssessmentForm from '../components/assessment/NurseAssessmentForm'
import { buildDomains, TONE_COLOR, statusFromPct } from '../lib/functional'
import RequestCallModal from '../components/shared/RequestCallModal'
import { summarise, hasAnyContent } from '../lib/careLogSummary'

const SHIFT_LABEL = {
  morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening',
  night: 'Night', full_day: 'Full day',
}

const todayISO = () => new Date().toLocaleDateString('en-CA')
const iso = (d) => d.toLocaleDateString('en-CA')
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function initials(name) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
}

function moodTone(avg) {
  if (avg == null) return 'var(--border)'
  if (avg >= 3.5) return 'var(--green)'
  if (avg >= 2.5) return 'var(--amber)'
  return 'var(--danger)'
}

function adherenceTone(pct) {
  if (pct >= 85) return 'var(--green)'
  if (pct >= 70) return 'var(--amber)'
  return 'var(--danger)'
}

const TABS = [
  ['today', 'Today'],
  ['week', 'Week'],
  ['month', 'Month'],
  ['function', 'Function'],
]

export default function TrackingPage() {
  const { recipient, role } = useCare()
  const [params, setParams] = useSearchParams()
  const tab = TABS.some(([t]) => t === params.get('tab')) ? params.get('tab') : 'today'
  const setTab = (t) => setParams({ tab: t }, { replace: true })

  const [loading, setLoading] = useState(true)
  const [d, setD] = useState(null)
  const [showVitals, setShowVitals] = useState(false)
  const [showAssessment, setShowAssessment] = useState(false)
  const [callModal, setCallModal] = useState(null)

  const canAssess = role === 'admin' || role === 'nurse'
  const today = todayISO()

  const load = useCallback(async () => {
    if (!recipient) return
    setLoading(true)
    const rid = recipient.id

    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 6)
    const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30)

    const [logsToday, vitalsToday, incToday, weekLogs, meds, medLogsWeek, incWeek, progress, assessment, specialists] =
      await Promise.all([
        supabase.from('care_logs')
          .select('*, author:profiles!logged_by(full_name)')
          .eq('care_recipient_id', rid).eq('log_date', today)
          .order('created_at', { ascending: true }),
        supabase.from('vitals_logs').select('*').eq('care_recipient_id', rid).eq('log_date', today)
          .order('created_at', { ascending: false }),
        supabase.from('incidents').select('*').eq('care_recipient_id', rid).eq('incident_date', today),
        supabase.from('care_logs')
          .select('log_date, mood_morning, mood_afternoon, mood_evening')
          .eq('care_recipient_id', rid).gte('log_date', iso(weekAgo)),
        supabase.from('medications').select('id, name').eq('care_recipient_id', rid).eq('is_active', true),
        supabase.from('medication_logs').select('medication_id, status, log_date')
          .eq('care_recipient_id', rid).gte('log_date', iso(weekAgo)),
        supabase.from('incidents').select('*').eq('care_recipient_id', rid).gte('incident_date', iso(weekAgo))
          .order('incident_date', { ascending: false }),
        supabase.from('monthly_progress').select('*').eq('care_recipient_id', rid)
          .order('month', { ascending: false }).limit(2),
        supabase.from('functional_assessments').select('*').eq('care_recipient_id', rid)
          .order('assessment_date', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('care_specialists').select('name, specialty').eq('care_recipient_id', rid).limit(1),
      ])

    setD({
      logsToday: logsToday.data || [],
      vitalsToday: vitalsToday.data || [],
      incToday: incToday.data || [],
      weekLogs: weekLogs.data || [],
      meds: meds.data || [],
      medLogsWeek: medLogsWeek.data || [],
      incWeek: incWeek.data || [],
      progress: progress.data || [],
      assessment: assessment.data || null,
      doctor: (specialists.data || [])[0] || null,
      monthAgoISO: iso(monthAgo),
    })
    setLoading(false)
  }, [recipient, today])

  useEffect(() => { load() }, [load])

  if (loading || !d) return <LoadingSpinner label="Loading tracking…" />

  return (
    <div className="stack">
      <h1 className="title">Tracking</h1>

      <div className="segmented">
        {TABS.map(([v, l]) => (
          <button key={v} className={tab === v ? 'on' : ''} onClick={() => setTab(v)}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'today' && <TodayTab d={d} onLogVital={() => setShowVitals(true)} />}
      {tab === 'week' && <WeekTab d={d} />}
      {tab === 'month' && <MonthTab d={d} />}
      {tab === 'function' && (
        <FunctionTab
          d={d}
          recipient={recipient}
          canAssess={canAssess}
          onRunTest={() => setShowAssessment(true)}
        />
      )}

      <CoordinatorCTA
        name={recipient?.preferred_name || recipient?.full_name || 'them'}
        onGetStarted={() => setCallModal('connect')}
        onBookCall={() => setCallModal('unsure')}
      />

      {callModal && (
        <RequestCallModal plan={callModal} onClose={() => setCallModal(null)} />
      )}

      {showVitals && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setShowVitals(false)}>
          <div className="modal-panel">
            <VitalsForm
              recipientId={recipient.id}
              onCancel={() => setShowVitals(false)}
              onDone={() => { setShowVitals(false); load() }}
            />
          </div>
        </div>
      )}

      {showAssessment && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setShowAssessment(false)}>
          <div className="modal-panel">
            <NurseAssessmentForm
              recipientId={recipient.id}
              onCancel={() => setShowAssessment(false)}
              onDone={() => { setShowAssessment(false); load() }}
            />
          </div>
        </div>
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}


/* ── Upgrade CTA (below every sub-tab) ──────────────────────── */

function CoordinatorCTA({ name, onGetStarted, onBookCall }) {
  return (
    <div className="card" style={{ borderColor: 'var(--pa)', borderWidth: 1.5 }}>
      <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 20, lineHeight: 1.2 }} aria-hidden>📞</span>
        <div>
          <div style={{ fontWeight: 700, lineHeight: 1.4 }}>
            Want someone calling {name} every day?
          </div>
          <div className="muted" style={{ fontSize: 14, marginTop: 4, lineHeight: 1.55 }}>
            This is remote care — a coordinator calls by phone, a nurse meets by
            video. No home visits.
          </div>
        </div>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 14 }}>
        <button className="btn btn-sm grow" onClick={onGetStarted}>
          Get started — $197/month →
        </button>
        <button type="button" className="btn btn-sm btn-secondary grow" onClick={onBookCall}>
          Book a free call →
        </button>
      </div>
    </div>
  )
}

/* ── TODAY ──────────────────────────────────────────────────── */

/**
 * "What was logged today".
 *
 * A day can hold several care logs — different shifts, different people — so
 * each log renders as its own sub-section under its shift badge rather than
 * being flattened into one row. Medications and incidents are day-level and
 * sit in a footer.
 */
const TONE_BG = {
  green: { background: 'var(--green-light)', color: 'var(--green)' },
  amber: { background: 'var(--amber-light)', color: 'var(--amber)' },
  danger: { background: 'var(--danger-light)', color: 'var(--danger)' },
  grey: { background: '#ECEDEC', color: 'var(--muted)' },
}

function LogSection({ log, showShift }) {
  const s = summarise(log)
  const mealsFallback =
    !s.mealEntries.length && log.meals_notes ? String(log.meals_notes).trim() : null

  return (
    <div className="stack" style={{ gap: 6 }}>
      {showShift && (
        <div className="row wrap" style={{ gap: 6 }}>
          <span className="badge" style={{ textTransform: 'capitalize' }}>
            {SHIFT_LABEL[log.shift] || log.shift || 'Log'}
          </span>
          <span className="muted" style={{ fontSize: 12 }}>
            logged by {log.author?.full_name || 'someone'}
          </span>
        </div>
      )}

      {s.mealEntries.length > 0 && (
        <div className="row wrap" style={{ gap: 6 }}>
          {s.mealEntries.map((m) => (
            <span
              key={m.key}
              className="badge"
              style={TONE_BG[m.tone] || TONE_BG.grey}
              title={m.detail || m.statusLabel || ''}
            >
              {m.emoji} {m.label} {m.icon || ''}
            </span>
          ))}
        </div>
      )}

      {mealsFallback && (
        <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          🍽️ {mealsFallback}
        </div>
      )}

      <div className="row wrap" style={{ gap: 6 }}>
        {s.water && <span className="badge">💧 {s.water}</span>}
        {s.movement && <span className="badge">🚶 {s.movement}</span>}
        {s.sleep && <span className="badge">{s.sleepEmoji || '😴'} {s.sleep}</span>}
        {s.supplements > 0 && (
          <span className="badge">💊 {s.supplements} supplement{s.supplements > 1 ? 's' : ''}</span>
        )}
      </div>
    </div>
  )
}

function TodaySummary({ d }) {
  const logs = (d.logsToday || []).filter(hasAnyContent)

  const today = todayISO()
  const medsTotal = (d.meds || []).length
  const medsTaken = (d.medLogsWeek || []).filter(
    (l) => l.log_date === today && l.status === 'taken'
  ).length
  const incidents = (d.incToday || []).length

  if (logs.length === 0 && medsTaken === 0 && incidents === 0) {
    return (
      <div className="card muted" style={{ fontSize: 14 }}>
        Nothing logged yet today — use <strong>+</strong> to add a care note.
      </div>
    )
  }

  return (
    <div className="card stack" style={{ gap: 12 }}>
      {logs.map((log, i) => (
        <div
          key={log.id}
          style={i > 0 ? { paddingTop: 12, borderTop: '1px solid var(--border)' } : undefined}
        >
          <LogSection log={log} showShift={logs.length > 1 || !!log.shift} />
        </div>
      ))}

      {(medsTotal > 0 || incidents > 0) && (
        <div
          className="row wrap"
          style={{ gap: 6, paddingTop: logs.length ? 10 : 0, borderTop: logs.length ? '1px solid var(--border)' : 'none' }}
        >
          {medsTotal > 0 && (
            <span className="badge">💊 {medsTaken} of {medsTotal} meds taken</span>
          )}
          {incidents > 0 && (
            <span className="badge badge-danger">
              ⚠️ {incidents} incident{incidents > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function TodayTab({ d, onLogVital }) {
  const checkIn = d.logsToday.find((l) => l.shift === 'morning')
  const family = d.logsToday.filter((l) => l !== checkIn)

  return (
    <div className="stack">
      <div className="section-title">Coordinator check-in</div>
      {checkIn ? (
        <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
          <div
            aria-hidden
            style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              background: 'var(--pa)', color: '#fff', fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {initials(checkIn.author?.full_name) || 'MC'}
          </div>
          <div
            className="card"
            style={{ background: 'var(--pa-light)', borderColor: 'transparent', borderTopLeftRadius: 4 }}
          >
            <div className="spread">
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {checkIn.author?.full_name || 'Coordinator'}
              </div>
              <span className="muted" style={{ fontSize: 12 }}>
                {checkIn.created_at
                  ? new Date(checkIn.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                  : ''}
              </span>
            </div>
            {checkIn.notes && (
              <p style={{ fontSize: 14, marginTop: 6, lineHeight: 1.55 }}>{checkIn.notes}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="card muted" style={{ fontSize: 14 }}>No coordinator check-in logged today.</div>
      )}

      <div className="section-title">What was logged today</div>
      <TodaySummary d={d} />

      <div className="section-title">Family log</div>
      {family.length === 0 ? (
        <div className="card muted" style={{ fontSize: 14 }}>Nothing logged by the family today.</div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {family.map((l) => (
            <div key={l.id} className="card">
              <div className="spread">
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {l.author?.full_name || 'Family'}
                </div>
                <span className="badge" style={{ textTransform: 'capitalize' }}>{l.shift || 'log'}</span>
              </div>
              {l.notes && <p style={{ fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>{l.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="spread">
        <div className="section-title">Vitals today</div>
        <button
          onClick={onLogVital}
          style={{ background: 'none', border: 'none', color: 'var(--pa)', fontWeight: 600, fontSize: 14 }}
        >
          + Log a vital
        </button>
      </div>
      {d.vitalsToday.length === 0 ? (
        <div className="card muted" style={{ fontSize: 14 }}>No vitals recorded today.</div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {d.vitalsToday.map((v) => {
            const flag = bpFlag(v.bp_systolic, v.bp_diastolic)
            return (
              <div key={v.id} className={`card${flag === 'high' ? ' tint-medium' : ''}`}>
                <div className="row wrap" style={{ gap: 14 }}>
                  {v.bp_systolic != null && (
                    <Metric
                      label="Blood pressure"
                      value={`${v.bp_systolic}/${v.bp_diastolic}`}
                      color={flag === 'high' ? 'var(--amber)' : undefined}
                    />
                  )}
                  {v.fluids_ml != null && <Metric label="Fluids" value={`${v.fluids_ml} ml`} />}
                  {v.blood_glucose != null && <Metric label="Glucose" value={`${v.blood_glucose}`} />}
                  {v.weight_kg != null && <Metric label="Weight" value={`${v.weight_kg} kg`} />}
                </div>
                {flag === 'high' && (
                  <div style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600, marginTop: 8 }}>
                    ⚠️ Above 140/90 — worth mentioning to the GP
                  </div>
                )}
                {v.notes && <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>{v.notes}</p>}
              </div>
            )
          })}
        </div>
      )}

      {d.incToday.length > 0 && (
        <>
          <div className="section-title">Incidents today</div>
          <div className="stack" style={{ gap: 8 }}>
            {d.incToday.map((i) => <IncidentCard key={i.id} i={i} />)}
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ label, value, color }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 17, color: color || 'var(--text)' }}>{value}</div>
    </div>
  )
}

function IncidentCard({ i }) {
  const tint = i.severity === 'serious' ? 'tint-high' : i.severity === 'moderate' ? 'tint-medium' : ''
  return (
    <div className={`card ${tint}`}>
      <div className="spread">
        <span className={`prio-label prio-${i.severity === 'serious' ? 'high' : i.severity === 'moderate' ? 'medium' : 'ongoing'}`}>
          {i.severity}
        </span>
        <span className="muted" style={{ fontSize: 12 }}>
          {i.incident_time ? i.incident_time.slice(0, 5) : i.incident_date}
        </span>
      </div>
      <p style={{ fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>{i.description}</p>
      {i.injury && (
        <span className="badge badge-danger mt-8">
          Injury{i.injury_description ? ` — ${i.injury_description}` : ''}
        </span>
      )}
    </div>
  )
}

/* ── WEEK ───────────────────────────────────────────────────── */

function WeekTab({ d }) {
  // Mon–Sun buckets for the last 7 days.
  const buckets = DAYS.map(() => [])
  for (const l of d.weekLogs) {
    const dt = new Date(l.log_date + 'T00:00:00')
    const idx = (dt.getDay() + 6) % 7 // Mon = 0
    for (const m of [l.mood_morning, l.mood_afternoon, l.mood_evening]) {
      if (m != null) buckets[idx].push(m)
    }
  }
  const avgs = buckets.map((b) => (b.length ? b.reduce((x, y) => x + y, 0) / b.length : null))

  // Medication adherence for the week, per medication.
  const takenByMed = {}
  for (const r of d.medLogsWeek) {
    if (r.status && r.status !== 'taken') continue
    takenByMed[r.medication_id] = (takenByMed[r.medication_id] || 0) + 1
  }

  return (
    <div className="stack">
      <div className="section-title">Mood this week</div>
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, alignItems: 'end', height: 120 }}>
          {avgs.map((a, i) => (
            <div key={DAYS[i]} style={{ textAlign: 'center' }}>
              <div style={{ height: 92, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                <div
                  title={a ? `${Math.round(a * 10) / 10}/5` : 'No data'}
                  style={{
                    width: '72%',
                    height: a ? `${(a / 5) * 100}%` : 4,
                    minHeight: 4,
                    borderRadius: 5,
                    background: moodTone(a),
                    opacity: a ? 1 : 0.5,
                  }}
                />
              </div>
              <div className="muted" style={{ fontSize: 10, marginTop: 5 }}>{DAYS[i]}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="section-title">Medication adherence</div>
      {d.meds.length === 0 ? (
        <div className="card muted" style={{ fontSize: 14 }}>No active medications.</div>
      ) : (
        <div className="card stack" style={{ gap: 12 }}>
          {d.meds.map((m) => {
            const taken = takenByMed[m.id] || 0
            const pct = Math.min(100, Math.round((taken / 7) * 100))
            return (
              <div key={m.id}>
                <div className="spread" style={{ marginBottom: 5 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: adherenceTone(pct) }}>
                    {taken}/7
                  </span>
                </div>
                <div className="meter">
                  <span style={{ width: `${pct}%`, background: adherenceTone(pct) }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="section-title">Incidents this week</div>
      {d.incWeek.length === 0 ? (
        <div className="card" style={{ fontSize: 14, color: 'var(--green)', fontWeight: 600 }}>
          None this week ✓
        </div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          <div style={{ fontWeight: 700 }}>{d.incWeek.length} logged</div>
          {d.incWeek.map((i) => <IncidentCard key={i.id} i={i} />)}
        </div>
      )}
    </div>
  )
}

/* ── MONTH ──────────────────────────────────────────────────── */

// Column names match monthly_progress (domain_*, scored 1–5).
// "Medications" has no stored column — it is derived from medication_logs.
const DOMAINS = [
  ['domain_memory', 'Memory'],
  ['domain_mobility', 'Mobility'],
  ['domain_mood', 'Mood'],
  ['medications', 'Medications'],
  ['domain_social_engagement', 'Social engagement'],
  ['domain_sleep', 'Sleep'],
  ['domain_appetite', 'Appetite'],
  ['domain_pain', 'Pain'],
  ['domain_personal_care', 'Personal care'],
  ['domain_communication', 'Communication'],
]

const TRAJECTORY = {
  improvement: { label: 'Improving', cls: 'badge-green' },
  stable: { label: 'Stable', cls: 'badge' },
  gradual_decline: { label: 'Gradual decline', cls: 'badge-amber' },
  acute_change: { label: 'Acute change', cls: 'badge-danger' },
}

function MonthTab({ d }) {
  const navigate = useNavigate()
  const [cur, prev] = d.progress
  const traj = TRAJECTORY[cur?.trajectory] || TRAJECTORY.stable

  // Medication adherence is derived, not stored on monthly_progress.
  const medPct = (() => {
    if (!d.meds.length) return null
    const taken = d.medLogsWeek.filter((r) => !r.status || r.status === 'taken').length
    return Math.min(100, Math.round((taken / (d.meds.length * 7)) * 100))
  })()

  if (!cur) {
    return (
      <div className="stack">
        <div className="card muted" style={{ fontSize: 14 }}>
          No monthly assessment recorded yet. Once a month is scored, the
          10-domain comparison and doctor summary appear here.
        </div>
      </div>
    )
  }

  return (
    <div className="stack">
      <div className="spread">
        <div className="section-title">This month</div>
        <span className={`badge ${traj.cls}`}>{traj.label}</span>
      </div>

      <div className="card stack" style={{ gap: 12 }}>
        {DOMAINS.map(([key, label]) => {
          const isMeds = key === 'medications'
          const raw = isMeds ? (medPct == null ? null : medPct / 20) : cur[key]
          const prevRaw = isMeds ? null : prev?.[key]
          if (raw == null) {
            return (
              <div key={key} className="spread">
                <span style={{ fontSize: 14 }}>{label}</span>
                <span className="muted" style={{ fontSize: 13 }}>Not scored</span>
              </div>
            )
          }
          const pct = isMeds ? medPct : Math.round((raw / 5) * 100)
          const delta = prevRaw != null ? raw - prevRaw : null
          const arrow = delta == null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '→'
          const arrowColor =
            delta == null ? 'var(--muted)' : delta > 0 ? 'var(--green)' : delta < 0 ? 'var(--danger)' : 'var(--muted)'
          return (
            <div key={key}>
              <div className="spread" style={{ marginBottom: 5 }}>
                <span style={{ fontSize: 14 }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: arrowColor }}>
                  {isMeds ? `${pct}%` : `${raw}/5`} {arrow}
                </span>
              </div>
              <div className="meter">
                <span style={{ width: `${pct}%` }} />
              </div>
            </div>
          )
        })}
      </div>

      <div className="card">
        <div className="section-title">Doctor summary</div>
        <div style={{ fontWeight: 700, marginTop: 4 }}>
          30-day summary ready{d.doctor?.name ? ` for ${d.doctor.name}` : ''}
        </div>
        <div className="row no-print" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn-sm grow" onClick={() => navigate('/reports')}>
            Print PDF
          </button>
          <button className="btn btn-sm btn-secondary grow" onClick={() => navigate('/reports')}>
            Email
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── FUNCTION ───────────────────────────────────────────────── */

function FunctionTab({ d, recipient, canAssess, onRunTest }) {
  const domains = buildDomains(recipient, d.assessment)
  const declining = domains.filter((x) => x.tone === 'bad')

  const nextDate = (() => {
    const base = d.assessment?.assessment_date
      ? new Date(d.assessment.assessment_date + 'T00:00:00')
      : new Date()
    base.setMonth(base.getMonth() + 1)
    return base.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
  })()

  return (
    <div className="stack">
      {declining.length > 0 && (
        <div className="card tint-high">
          <div style={{ fontWeight: 700, color: 'var(--danger)' }}>
            ⚠️ {declining.map((x) => x.name).join(' and ')} declining
          </div>
          <div style={{ fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>
            Worth raising at the next appointment.
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {domains.map((x) => {
          const s = statusFromPct(x.pct)
          const color = TONE_COLOR[x.tone] || 'var(--muted)'
          return (
            <div key={x.key} className="card" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 8 }}>
                <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>{x.emoji}</span>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{x.name}</span>
              </div>
              <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.45, minHeight: 34 }}>
                {x.text}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color, marginTop: 4 }}>
                {x.pct == null ? '' : `${s.label} ${s.arrow}`}
              </div>
              <div className="meter" style={{ marginTop: 8 }}>
                <span style={{ width: `${x.pct ?? 0}%`, background: color }} />
              </div>
            </div>
          )
        })}
      </div>

      <div className="card">
        <div className="section-title">Monthly assessment</div>
        <div style={{ fontWeight: 700, marginTop: 4 }}>Next due {nextDate}</div>
        <div className="muted" style={{ fontSize: 14, marginTop: 2 }}>
          {d.assessment
            ? `Last run ${new Date(d.assessment.assessment_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
            : 'Not run yet'}
        </div>
        {canAssess ? (
          <button className="btn btn-block mt-16" onClick={onRunTest}>Run test →</button>
        ) : (
          <div className="hint mt-8">Only an admin or nurse can run the assessment.</div>
        )}
      </div>
    </div>
  )
}
